// src/pages/negative/NegativeCreatePage.jsx — React Hook Form + React Query + Pré-check Indireto (completo)
import React from "react";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Card,
  Label,
  TextInput,
  Select,
  Alert,
  Modal,
} from "flowbite-react";
import {
  HiLightningBolt,
  HiInformationCircle,
  HiCheckCircle,
  HiArrowLeft,
  HiArrowRight,
  HiX,
} from "react-icons/hi";
import axios from "../../config/axiosInstance";
import { NEGATIVE } from "../../config/apiConfig";
import { useApplication } from "../../contexts/ApplicationContext";
import ForbiddenQueriesPermission from "../common/ForbiddenQueriesPermission";
import { useForm, Controller, useWatch, useFieldArray } from "react-hook-form";
import {
  maskCNPJ,
  maskDocument,
  getDocType,
  onlyCnpjChars,
} from "../../utils/masks";
import { isValidCPF, isValidCNPJ } from "../../utils/utils";

/* ===========================
 * Constantes / Opções
 * =========================== */
const NATUREZA_RAW = `
Código\tDescrição da Natureza\tAbreviação
ABI\tAdiantamento a Depositante-Banco de Investimento\tAdiant Dep Bco Inv
ACV\tAdiantamento a Depositante-Corretora de Valores\tAdiant Dep Cor Val
ACI\tAdiantamento a Depositante-Crédito Imobiliário\tAdiant Dep Cred Im
AF\tAdiantamento a Depositante-Financeira\tAdiant Dep Finan
ACC\tAdiantamento a Depositantes - Conta Corrente Devedora\tAdiant Dep CC Dev
AS\tAdiantamento a Depositante-Seguradora\tAdiant Dep Segur
ASP\tAdiantamento a Depositante-Sociedade de Poupança\tAdiant Dep Soc Poup
AT\tAdiantamento a Depositante-Turismo\tAdiant Dep Turismo
AD\tAdiantamento de Conta\tAdiant Conta
AL\tAluguel\tAluguel
AP\tAp Seguro\tAp Seguro
APS\tArrecadadores-Prestadores de Serviços\tArrecadador
AR\tArrendamento, Inclusive Leasing\tArrend Leasing
ALI\tArrendamentos Inclusive Leasing-Banco de Investimento\tLeasing Bco Invest
ALV\tArrendamentos Inclusive Leasing-Corretora de Valores\tLeasing Cor Val
ALC\tArrendamentos Inclusive Leasing-Crédito Imobiliário\tLeasing Cred Imob
ALF\tArrendamentos Inclusive Leasing-Financeira\tLeasing Financ
ALS\tArrendamentos Inclusive Leasing-Seguradora\tLeasing Segur
ALP\tArrendamentos Inclusive Leasing-Sociedade de Poupança\tLeasing Soc Poup
ALT\tArrendamentos Inclusive Leasing-Turismo\tLeasing Turismo
BDP\tBoleto Proposta\tBoleto Proposta
C1\tConsórcop imóvel\tCONS_IMOVEL
C2\tConsórcio Veículos Pesados: Tratores, Ônibus, Caminhões, Barcos e Aviões\tCONS_PESADOS
C3\tConsórcio Veículos\tCONS_VEICULO
C4\tConsórcio motocicletas e motonetas\tCONS_MOTO
C5\tConsórcio outros bens moveis\tCONS_BENSMOV
C6\tConsórcio passagens aéras\tCONS_PASSAER
CA\tOperações de Financiamento de Câmbio Em Geral\tOP_FINCAMB
CB\tContrato de cambio\tCONTR_CAMBIO
CDB\tCDC Bens\tCDC Bens
CDM\tCDC Motos\tCDC Motos
CDL\tCDC Veículos Leves\tCDC Veic Leve
CDP\tCDC Veículos Pesados\tCDC Veic Pes
CH\tCheque\tCheque
CHQ\tCheque\tCheque
CI\tCédula de credito industrial\tCEDU_CREDIND
CL\tCDC veículos leves\tCDC_VEICLEVE
CO\tConsórcio Contemplado\tCons Contemp
CM\tConsórcio Imóvel\tCons Imoveis
CMO\tConsórcio Motocicletas e Motonetas\tCons Motos
CBE\tConsórcio Outros Bens Móveis\tCons Bens
CP\tCrédito pessoal\tCRED_PESSOAL
CPA\tConsórcio Passagens Aéreas\tCons Aereo
CR\tCédula rural pignoratícia (impedidos de créditos rural pelo Bacen)\tCEDU_RUR_IMP
CS\tContratos (diversos)\tCONTR_DIVERS
CT\tOperações com Cartão de Crédito\tOPER_CARTCRE
CV\tCDC veículos pesados: tratores, onibus, caminhoes, barcos e avioes\tCDC_VEICLEVE
CVE\tConsórcio Veículos\tCons Veiculo
CVP\tConsórcio Veículos Pesados: Tratores, Ônibus, Caminhões, Barcos e Aviões\tCons Vei Pes
CC\tCota Condominial\tCondominio
CRC\tCrédito Cartão\tCred Cartao
CD\tCrédito Direto – Crediário\tCrediario
CFB\tCrédito e Financiamento-Banco de Investimento\tCred Fin Bco Inv
CFV\tCrédito e Financiamento-Corretora de Valores\tCred Fin Cor Val
CFM\tCrédito e Financiamento-Crédito Imobiliário\tCred Fin Cred Imob
CFF\tCrédito e Financiamento-Financeira\tCred Fin Financeira
CFS\tCrédito e Financiamento-Seguradora\tCred Fin Seguradora
CFP\tCrédito e Financiamento-Sociedade de Poupança\tCred Fin Soc Poup
CFT\tCrédito e Financiamento-Turismo\tCred Fin Turismo
CP\tCrédito Pessoal\tCred Pessoal
CFI\tCréditos e Financiamentos\tCréd. E Financiamento
DAE\tDivida At Estadual\tDpivida At Estadual
DAM\tDivida At Municipal\tDivida At Municipal
DAU\tDivida At Uniao\tDivida At Uniao
DB\tDebêntures\tDEBENTURES
HO\tDespesas com Hospitais\tHospitais
DE\tDívidas Originárias de Cheque Eletrônico\tCheque Elet
DC\tDívidas Originárias de Cheques\tDividas Cheq
DM\tDup Mercantil\tDup Mercantil
DP\tDuplicata\tDUPLICATAS
DR\tDup Rural\tDup Rural
DS\tDup Serviço\tDup Serviço
DSI\tDuplicata de Serviços Indicação\tDup Servico Indicacao
EP\tEmpréstimo\tEmprestimos
AG\tEmpréstimo / Financiamento Agrícola / Rural-Comercial, Industrial E Custeio\tEmpr Fin Agr
EG\tEmpréstimo Consignado\tEmpr Consig
EC\tEmpréstimo em Conta\tEmprestimo Cta
ECI\tEmpréstimo em Conta-Banco de Investimento\tEmpres Cta Bco Inv
ECV\tEmpréstimo em Conta-Corretora de Valores\tEmpres Cta Cor Val
ECM\tEmpréstimo em Conta-Crédito Imobiliário\tEmpres Cta Cred Imob
ECF\tEmpréstimo em Conta-Financeira\tEmpres Cta Financeira
ECS\tEmpréstimo em Conta-Seguradora\tEmpres Cta Seguradora
ECP\tEmpréstimo em Conta-Sociedade de Poupança\tEmpres Cta Soc Poup
ECT\tEmpréstimo em Conta-Turismo\tEmpres Cta Turismo
EAB\tEmpréstimos Agrícolas e Industriais-Banco de Investimento\tEmpr Agr Bco Inv
EAC\tEmpréstimos Agrícolas e Industriais-Corretora de Valores\tEmpr Agr Cor Val
EAI\tEmpréstimos Agrícolas e Industriais-Crédito Imobiliário\tEmpr Agr Cred Imob
EAF\tEmpréstimos Agrícolas e Industriais-Financeira\tEmpr Agr Financ
EAS\tEmpréstimos Agrícolas e Industriais-Seguradora\tEmpr Agr Seguradora
EAP\tEmpréstimos Agrícolas e Industriais-Sociedade de Poupança\tEmpr Agr Soc Poup
EAT\tEmpréstimos Agrícolas e Industriais-Turismo\tEmpr Agr Turismo
ECC\tEmpréstimos Em Conta, C/C Garantida, Capital De Giro, Programa Especial\tEmpres Conta
EE\tEnergia Elétrica-Faturas de Fornecimento De Luz\tEnergia Elet
ENJ\tExecução - Negativacao Judicial\tExec Neg Judic
FI\tFinanciamentos\tFinanciamentos
FG\tGás-Faturas de Fornecimento de Gás\tFat Gas
CR\tImpedidos de Crédito Rural Pelo Bacen\tImpedido Bc
IM\tOperações Imobiliárias\tOP_IMOBILIAR
IRI\tImpedimento pelo BC Obtenção de Crédito Rural-Banco de Investimento\tImp BC Bco Invest
IRV\tImpedimento pelo BC Obtenção de Crédito Rural-Corretora de Valores\tImp BC Cor Val
IRC\tImpedimento pelo BC Obtenção de Crédito Rural-Crédito Imobiliário\tImp BC Cred Imob
IRF\tImpedimento pelo BC Obtenção de Crédito Rural-Financeira\tImp BC Financeira
IRS\tImpedimento pelo BC Obtenção de Crédito Rural-Seguradora\tImp BC Seguradora
IRP\tImpedimento pelo BC Obtenção de Crédito Rural-Sociedade de Poupança\tImp BC Soc Poup
IRT\tImpedimento pelo BC Obtenção de Crédito Rural-Turismo\tImp BC Turismo
IBI\tIndisponibilidade de  Bens - Banco de Investimento\tInd Bem Bco Invest
IBG\tIndisponibilidade de  Bens - C/C Devedores em Geral\tInd Bem CC Dev
IBC\tIndisponibilidade de  Bens - Corretora de Valores\tInd Bem Cor Val
IBM\tIndisponibilidade de  Bens - Crédito Imobiliário\tInd Bem Cred Imob
IBF\tIndisponibilidade de  Bens - Financeira\tInd Bem Financeira
IBS\tIndisponibilidade de  Bens - Seguradora\tInd Bem Seguradora
IBP\tIndisponibilidade de  Bens - Sociedade de Poupança\tInd Bem Soc Poup
IBT\tIndisponibilidade de  Bens - Turismo\tInd Bem Turismo
IE\tInstituição de Ensino: Bolsa Restituível\tInst Ensino
IPV\tIPVA\tIPVA
LC\tLetra de cambio\tLETRA_CAMBIO
LEA\tLeasing\tLeasing
LM\tLeasing Motos\tLeasing Moto
LV\tLeasing Veículo\tLeasing Veic
LVP\tLeasing Veículos Pesados\tLeasing Veic Pes
SS\tMensalidade de Plano/Seguro de Saúde\tSeguro Saude
ME\tMensalidades Escolares\tMens Escolar
NCI\tNota de Credito Industrial\tNota Cred Industrial
ND\tNota de Debito\tNota de Debito
NE\tNota de Credito Exportação\tNOTA_CREDEXP
NF\tNotas Fiscais\tNotas Fiscais
NP\tNota promissoria\tNOT_PROMIS
NPR\tNota Prom Rural\tNota Prom Rural
NS\tNota de Seguro\tNota de Seguro
OC\tOperação Cambio\tOper Cambio
POR\tOperação Prejuízo\tOper Prejuizo
OA\tOperações Agrícolas – Negociações Produtos Rurais-Insumos,Sementes etc.\tOper Agric
OJ\tOperações Ajuizadas\tOper Ajuizad
OAI\tOperações Ajuizadas-Banco de Investimento\tOper Ajuizad Bco Inv
OAV\tOperações Ajuizadas-Corretora de Valores\tOper Ajuizad Cor Val
OAM\tOperações Ajuizadas-Crédito Imobiliário\tOper Ajuizad Cred Imob
OAF\tOperações Ajuizadas-Financeira\tOper Ajuizad Financeira
OAS\tOperações Ajuizadas-Seguradora\tOper Ajuizad Seguradora
OAP\tOperações Ajuizadas-Sociedade de Poupança\tOper Ajuizad Soc Poup
OAT\tOperações Ajuizadas-Turismo\tOper Ajuizad Turismo
OCI\tOperações de Câmbio-Banco de Investimento\tOper Bco Invest
OCV\tOperações de Câmbio-Corretora de Valores\tOper Cor Val
OCM\tOperações de Câmbio-Crédito Imobiliário\tOper Cred Imob
OCF\tOperações de Câmbio-Financeira\tOper Financeira
OCS\tOperações de Câmbio-Seguradora\tOper Seguradora
OCP\tOperações de Câmbio-Sociedade de Poupança\tOper Soc Poup
OCT\tOperações de Câmbio-Turismo\tOper Turismo
OO\tOutras operações que não se enquadram nas demais\tOP_DIVERSAS
ORE\tOperações De Repasse, Finame, 63, Recon Etc.\tRepasse Finame
ORI\tOperações de Repasse-Banco de Investimento\tOper Rep Bco Inv
ORV\tOperações de Repasse-Corretora de Valores\tOper Rep Cor Val
ORM\tOperações de Repasse-Crédito Imobiliário\tOper Rep Cred Imob
ORF\tOperações de Repasse-Financeira\tOper Rep Financeira
ORS\tOperações de Repasse-Seguradora\tOper Rep Seguradora
ORP\tOperações de Repasse-Sociedade de Poupança\tOper Rep Soc Poup
ORT\tOperações de Repasse-Turismo\tOper Rep Turismo
IM\tOperações Imobiliárias\tOper Imobil
OQI\tOperações Quitadas com Prejuízo-Banco de Investimento\tOper Quit Bco Inv
OQV\tOperações Quitadas com Prejuízo-Corretora de Valores\tOper Quit Cor Val
OQM\tOperações Quitadas com Prejuízo-Crédito Imobiliário\tOper Quit Cred Imob
OQF\tOperações Quitadas com Prejuízo-Financeira\tOper Quit Financeira
OQS\tOperações Quitadas com Prejuízo-Seguradora\tOper Quit Seguradora
OQP\tOperações Quitadas com Prejuízo-Sociedade de Poupança\tOper Quit Soc Poup
OQT\tOperações Quitadas com Prejuízo-Turismo\tOper Quit Turismo
OOI\tOutras  Operações-Banco de Investimento\tOutras Oper Bco Inv
OOV\tOutras  Operações-Corretora de Valores\tOutras Oper Cor Val
OOM\tOutras  Operações-Crédito Imobiliário\tOutras Oper Cred Imob
OOF\tOutras  Operações-Financeira\tOutras Oper Financeira
OOS\tOutras  Operações-Seguradora\tOutras Oper Seguradora
OOP\tOutras  Operações-Sociedade de Poupança\tOutras Oper Soc Poup
OOT\tOutras  Operações-Turismo\tOutras Oper Turismo
PGI\tPrejuízos em Geral-Banco de Investimento\tPrej Ger Bco Inv
PGV\tPrejuízos em Geral-Corretora de Valores\tPrej Ger Cor Val
PGM\tPrejuízos em Geral-Crédito Imobiliário\tPrej Ger Cred Imob
PGF\tPrejuízos em Geral-Financeira\tPrej Ger Financeira
PGS\tPrejuízos em Geral-Seguradora\tPrej Ger Seguradora
PGP\tPrejuízos em Geral-Sociedade de Poupança\tPrej Ger Soc Poup
PGT\tPrejuízos em Geral-Turismo\tPrej Ger Turismo
QP\tQuitação de Prejuízos\tQuit Prej
RC\tRecibo\tRecibo
RE\tRepasses\tRepasses
SB\tSaneamento Básico-Faturas de Fornecimento de Água\tFat Agua
SR\tSeguro de Risco Decorrido\tSeguro Risco
SF\tSeguro Fiança Locatícia\tSeg Fianca
SG\tSeguro Garantia\tSeg Garantia
SQ\tSeguro Quebra de Garantia\tSeg Quebra
SRD\tSeguro Risco Decorrido\tSeg Risco Dec
TI\tServiços de Dados e Internet (Speedy, Aluguel de Canais de Dados e Fibra Ótica )\tServ Dados
TR\tServiços de Telefonia – Renegociação da Dívida\tReneg Divida
TP\tServ. de Tel. / Vendas de Prod. – Vendas de Aparelhos, Inst., Aluguel de Aparelhos\tServ Telefon
TF\tServiços de Telefonia Fixa – Outras Contas Após Rescisão do Contrato\tTelef Fixa Outras
TT\tServiços de Telefonia Fixa – Res 085/98\tTelef Fixa
TM\tServiços de Telefonia Móvel Pessoal / Celular – Res221/00\tTelef Movel
TE\tServ. de Telef. Móvelpessoal / Celular – Outras Contas Após Rescisão do Contrato.\tTelef Movel Outras
TC\tTermo De Confissão de Dívida Descumprido\tConfiss Div
TD\tTítulos Descontados\tTit Desconta
TDU\tTítulos Descontados, Duplicatas, Promissórias etc.\tTit Desconta Dup
TDI\tTítulos Descontados-Banco de Investimento\tTit Desconta Bco Inv
TDV\tTítulos Descontados-Corretora de Valores\tTit Desconta Cor Val
TDM\tTítulos Descontados-Crédito Imobiliário\tTit Desconta Cred Imob
TDF\tTítulos Descontados-Financeira\tTit Desconta Financeira
TDS\tTítulos Descontados-Seguradora\tTit Desconta Seguradora
TDP\tTítulos Descontados-Sociedade de Poupança\tTit Desconta Soc Poup
TDT\tTítulos Descontados-Turismo\tTit Desconta Turismo
TS\tTriplicata de Serviço\tTrip Servico
DMI\tDuplicata Mercantil Indicação\tDup Merc Indicacao
DV\tDiversos\tDiversos
NCC\tNota de Crédito Comercial\tNota Cred Comercial
NCE\tNota de Crédito Exportação\tNota Cred Exportacao
NCI\tNota de Crédito Industrial\tNota Cred Industrial
TM\tServiços de Telefonia Móvel Pessoal / Celular – Res221/00\tTelef Movel
VM\tPrest Serv Vem Merc\tPrest Serv Vem Merc
VW\tPrestação de Serviços-Vendas de Mercadorias\tVenda Mercad
WR\tWarrant\tWarrant
OUT\tOUTRAS ATIVIDADES\tOUTRAS ATIVIDADES
AGR\tAGRICULTURA, PECUARIA E SERVICOS RELACIONADOS COM\tAGRICULTURA, PECUARI
SIL\tSILVICULTURA, EXPLORACAO FLORESTAL E SERVICOS RELA\tSILVICULTURA, EXPLOR
PES\tPESCA, AQUICULTURA E ATIVIDADES DOS SERVICOS RELAC\tPESCA, AQUICULTURA E
EXM\tEXTRACAO DE CARVAO MINERAL\tEXTRACAO DE CARVAO M
EXC\tEXTRACAO DE PETROLEO E SERVICOS CORRELATOS\tEXTRACAO DE PETROLEO
EXX\tEXTRACAO DE MINERAIS METALICOS\tEXTRACAO DE MINERAIS
EMM\tEXTRACAO DE MINERAIS NAO-METALICOS\tEXTRACAO DE MINERAIS
FMN\tFABRICACAO DE PRODUTOS ALIMENTICIOS E BEBIDAS\tFABRICACAO DE PRODUT
FFF\tFABRICACAO DE PRODUTOS DO FUMO\tFABRICACAO DE PRODUT
FAT\tFABRICACAO DE PRODUTOS TEXTEIS\tFABRICACAO DE PRODUT
CON\tCONFECCAO DE ARTIGOS DO VESTUARIO E ACESSORIOS\tCONFECCAO DE ARTIGOS
PRE\tPREPARACAO DE COUROS E FABRICACAO DE ARTEFATOS DE\tPREPARACAO DE COUROS
FBB\tFABRICACAO DE PRODUTOS DE MADEIRA\tFABRICACAO DE PRODUT
FBA\tFABRICACAO DE CELULOSE, PAPEL E PRODUTOS DE PAPEL\tFABRICACAO DE CELULO
EDI\tEDICAO, IMPRESSAO E REPRODUCAO DE GRAVACOES\tEDICAO, IMPRESSAO E
AAB\tFABRICACAO DE COQUE, REFINO DE PETROLEO, ELABORACA\tFABRICACAO DE COQUE,
FBQ\tFABRICACAO DE PRODUTOS QUIMICOS\tFABRICACAO DE PRODUT
FAP\tFABRICACAO DE ARTIGOS DE BORRACHA E PLASTICO\tFABRICACAO DE ARTIGO
FPP\tFABRICACAO DE PRODUTOS DE MINERAIS NAO-METALICOS\tFABRICACAO DE PRODUT
MET\tMETALURGIA BASICA\tMETALURGIA BASICA
FAE\tFABRICACAO DE PRODUTOS DE METAL-EXCLUSIVE MAQUINAS\tFABRICACAO DE PRODUT
FEE\tFABRICACAO DE MAQUINAS E EQUIPAMENTOS\tFABRICACAO DE MAQUIN
FME\tFABRICACAO DE MAQUINAS PARA ESCRITORIO E EQUIPAMEN\tFABRICACAO DE MAQUIN
FTE\tFABRICACAO DE MAQUINAS, APARELHOS E MATERIAIS ELET\tFABRICACAO DE MAQUIN
FDD\tFABRICACAO DE MATERIAL ELETRONICO E DE APARELHOS E\tFABRICACAO DE MATERI
FGG\tFABRICACAO DE EQUIPAMENTOS DE INSTRUMENTACAO MEDIC\tFABRICACAO DE EQUIPA
FCC\tFABRICACAO E MONTAGEM DE VEICULOS AUTOMOTORES, REB\tFABRICACAO E MONTAGE
FTT\tFABRICACAO DE OUTROS EQUIPAMENTOS DE TRANSPORTE\tFABRICACAO DE OUTROS
FMD\tFABRICACAO DE MOVEIS E INDUSTRIAS DIVERSAS\tFABRICACAO DE MOVEIS
REC\tRECICLAGEM\tRECICLAGEM
COP\tCONCESSIONARIA DE SERVIÇOS PÚBLICOS - ENERGIA\tCONCESSIONARIA DE SE
COA\tCONCESSIONARIA DE SERVIÇOS PÚBLICOS - ÁGUA\tCONCESSIONARIA DE SE
COT\tCONCESSIONARIA DE SERVIÇOS PÚBLICOS - ÁGUA\tCONSTRUÇÃO
COC\tCOMÉRCIO E REPARAÇÃO VEÍCULOS AUT. E MOT.\tCOMÉRCIO E REPARAÇÃO
CAT\tCOMERCIO ATACADISTA\tCOMERCIO ATACADISTA
COE\tCOMERCIO VAREJISTA\tCOMERCIO VAREJISTA
ALO\tALOJAMENTO E ALIMENTAÇÃO\tALOJAMENTO E ALIMENT
TRA\tTRANSPORTE TERRESTRE\tTRANSPORTE TERRESTRE
TRB\tTRANSPORTE AQUAVIÁRIO\tTRANSPORTE AQUAVIÁRI
TRC\tTRANSPORTE AÉREO\tTRANSPORTE AÉREO
ATI\tATIVIDADES AUXILIARES DOS TRANSP. E AGENCIAS\tATIVIDADES AUXILIARE
COR\tCORREIO E TELECOMUNICAÇÕES\tCORREIO E TELECOMUNI
INT\tINTERMEDIAÇÃO FINANCEIRA (BANCOS, CARTÕES ....)\tINTERMEDIAÇÃO FINANC
PLA\tPLANOS DE SAÚDE\tPLANOS DE SAÚDE
SEG\tSEGUROS/PREVIDÊNCIA PRIVADA/ADM.VALES ALIM...\tSEGUROS/PREVIDÊNCIA
ATA\tATIVIDADES IMOBILIÁRIAS\tATIVIDADES IMOBILIÁR
ALU\tALUGUEL DE VEÍCULOS, MAQ E EQUIPAMENTOS\tALUGUEL DE VEÍCULOS,
ATB\tATIVIDADES DE INFORMÁTICA E SERVIÇOS RELACIONADOS\tATIVIDADES DE INFORM
PED\tPESQUISA E DESENVOLVIMENTO\tPESQUISA E DESENVOLV
PRS\tPRESTAÇÃO DE SERVIÇOS\tPRESTAÇÃO DE SERVIÇO
EDU\tEDUCAÇÃO\tEDUCAÇÃO
SAL\tSAÚDE E SERVIÇOS SOCIAIS\tSAÚDE E SERVIÇOS SOC
LIM\tLIMPEZA URBANA E ESGOTO; E ATIVIDADES CONEXAS\tLIMPEZA URBANA E ESG
ATT\tATIVIDADES ASSOCIATIVAS\tATIVIDADES ASSOCIATI
ATC\tATIVIDADES RECREATIVAS, CULTURAIS E DESPORTIVAS\tATIVIDADES RECREATIV
SEA\tSERVICOS PESSOAIS\tSERVICOS PESSOAIS
SEB\tSERVICOS DOMESTICOS\tSERVICOS DOMESTICOS
ORG\tORGANISMOS INTERNACIONAIS E OUTRAS INSTITUICOES EX\tORGANISMOS INTERNACI
ADM\tADMINISTRACAO PUBLICA, DEFESA E SEGURIDADE SOCIAL\tADMINISTRACAO PUBLIC
AGG\tAGRICULTURA, PECUARIA E SERVICOS RELACIONADOS COM\tAGRICULTURA, PECUARI
ALG\tALUGUEL\tALUGUEL
ATD\tATIVIDADES ASSOCIATIVAS\tATIVIDADES ASSOCIATI
ATP\tATRASO DE PAGAMENTO\tATRASO DE PAGAMENTO
CTT\tCOMÉRCIO DE TINTAS\tCOMÉRCIO DE TINTAS
COD\tCONDOMINIO\tCONDOMINIO
CAA\tCONFECCAO DE ARTIGOS DO VESTUARIO E ACESSORIOS\tCONFECCAO DE ARTIGOS
CRD\tCREDIÁRIO\tCREDIÁRIO
RCO\tRELACAO COMERCIAL\tCREDIÁRIO
ENC\tENCARGOS COMNUNS\tENCARGOS COMNUNS
FLL\tFABRICACAO DE ARTIGOS DE BORRACHA E PLASTICO\tFABRICACAO DE ARTIGO
FNN\tFABRICACAO DE MOVEIS E INDUSTRIAS DIVERSAS\tFABRICACAO DE MOVEIS
FOO\tFABRICACAO DE OUTROS EQUIPAMENTOS DE TRANSPORTE\tFABRICACAO DE OUTROS
FGA\tFABRICACAO DE PRODUTOS DO FUMO\tFABRICACAO DE PRODUT
FAR\tFARMACIAS\tFARMACIAS
LIV\tLIVRARIAS\tLIVRARIAS
LOJ\tLOJAS DE CALÇADOS\tLOJAS DE CALÇADOS
OTI\tOTICAS\tOTICAS
PAG\tPAGAMENTO DE CHEQUES\tPAGAMENTO DE CHEQUES
PAR\tPARCELAMENTO DE DÍVIDA EM ABERTO\tPARCELAMENTO DE DÍVI
POS\tPOSTOS DE GASOLINA\tPOSTOS DE GASOLINA
PRO\tPRODUTOS DE BELEZA - COSMETICOS/PERFUMARIA\tPRODUTOS DE BELEZA
SER\tSERVICOS PESSOAIS\tSERVICOS PESSOAIS
SUP\tSUPERMERCADOS\tSUPERMERCADOS
VES\tVESTUARIOS\tVESTUARIOS
LEV\tLOCACAO DE EQUIPAMENTOS E VEICULOS\tLOC EQUIPAMENTOS E VEIC
`;

const NATUREZAS = NATUREZA_RAW.split(/\r?\n/)
  .map((line) => line.trim())
  .filter((line) => line && !/^c[oó]digo/i.test(line))
  .map((line) => {
    let parts = line.split("\t");
    if (parts.length < 2) parts = line.split(/\s{2,}/);
    parts = parts.map((p) => p.trim()).filter(Boolean);
    const codigo = String(parts[0] || "").trim();
    let descricao = "";
    let sigla = "";
    if (parts.length >= 3) {
      sigla = parts[parts.length - 1] || "";
      descricao = parts.slice(1, -1).join(" ").trim();
    } else {
      descricao = parts.slice(1).join(" ").trim();
    }
    return {
      Codigo: codigo,
      Descricao: descricao,
      Sigla: sigla,
    };
  })
  .filter((n) => n.Codigo);

const NATUREZA_BY_CODE = NATUREZAS.reduce((acc, n) => {
  const key = String(n.Codigo || "").toUpperCase();
  if (!acc[key]) acc[key] = n;
  return acc;
}, {});
const NATUREZA_MAX_RESULTS = 50;

const UFS = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

const BUREAUS = [
  {
    value: "QUOD",
    label: "Quod",
    logo: "/assets/logo_quod.png",
    available: true,
    description: "Disponível para negativação.",
  },
  {
    value: "BOA_VISTA",
    label: "Boa Vista",
    logo: "/assets/logo_bvs.png",
    available: false,
    description: "Integração em desenvolvimento.",
  },
];

function formatDateBR(value) {
  if (!value) return "—";
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}/${mm}/${yy}`;
}

/* ===========================
 * Helpers / máscaras / validações
 * =========================== */
function toYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function addDays(date, delta) {
  const d = new Date(date);
  d.setDate(d.getDate() + delta);
  return d;
}
function addMonths(date, delta) {
  const d = new Date(date);
  const day = d.getDate();
  d.setMonth(d.getMonth() + delta);
  if (d.getDate() < day) d.setDate(0);
  return d;
}
const CONTRATO_ALLOWED = /^[0-9A-Z/\-.]{1,16}$/;
function isAdult(dateStr) {
  if (!dateStr) return true;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  const limit = new Date(now.getFullYear() - 18, now.getMonth(), now.getDate());
  return d <= limit;
}
async function lookupCEP(cepDigits) {
  const res = await fetch(`https://viacep.com.br/ws/${cepDigits}/json/`);
  if (!res.ok) throw new Error("Falha ao consultar CEP");
  const data = await res.json();
  if (data.erro) throw new Error("CEP não encontrado");
  return data;
}
function formatBRLMask(input) {
  const digits = onlyDigits(input);
  if (!digits) return "";
  const cents = parseInt(digits, 10);
  const value = cents / 100;
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}
function parseBRLToNumber(masked) {
  const digits = onlyDigits(masked);
  if (!digits) return 0;
  const cents = parseInt(digits, 10);
  return Number.isFinite(cents) ? cents / 100 : 0;
}
const onlyDigits = (v) => (v == null ? "" : String(v)).replace(/\D+/g, "");
const normalizeStatus = (s) => String(s || "").toUpperCase();
// Documento do devedor pode ser CPF (só dígitos) ou CNPJ alfanumérico.
const maskCNPJorCPF = (v) => maskDocument(v);
const maskCEP = (v) =>
  onlyDigits(v)
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2-$3")
    .slice(0, 10);
const maskUF = (v) => (v || "").toUpperCase().slice(0, 2);
// maskCNPJ / isValidCPF / isValidCNPJ vêm dos utils canônicos (alfanuméricos).

/* ===========================
 * Componente
 * =========================== */
export default function NegativeCreatePage() {
  const { user } = useApplication();
  const navigate = useNavigate();
  const location = useLocation();
  const editId = location.state?.editId || null;
  const isEditMode = Boolean(editId);

  // Janela válida: [hoje - 4 anos e 11 meses,  hoje - 4 dias]
  const vencMinDate = React.useMemo(() => addMonths(new Date(), -59), []);
  const vencMaxDate = React.useMemo(() => addDays(new Date(), -5), []);
  const vencMin = React.useMemo(() => toYMD(vencMinDate), [vencMinDate]);
  const vencMax = React.useMemo(() => toYMD(vencMaxDate), [vencMaxDate]);

  /* ====== Form principal: Negativação ====== */
  const [successMsg, setSuccessMsg] = React.useState("");
  const [cepLoading, setCepLoading] = React.useState(false);
  const [cepError, setCepError] = React.useState("");

  // estados para lookup de CNPJ (OpenCNPJ)
  const [cnpjLookupLoading, setCnpjLookupLoading] = React.useState(false);
  const [cnpjLookupError, setCnpjLookupError] = React.useState("");
  const [naturezaOpen, setNaturezaOpen] = React.useState(false);

  const {
    control,
    handleSubmit,
    trigger,
    setValue,
    getValues,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
    criteriaMode: "firstError",
    shouldFocusError: true,
    defaultValues: {
      companyId: "",
      cnpj: "",
      documentoDevedor: "",
      nome: "",
      dataNascimento: "",
      contrato: "",
      valor: "",
      dataVencimento: vencMax,
      codigoNatureza: "",
      participacao: "",
      bureaus: [],
      // Endereço
      cep: "",
      endereco: "",
      numero: "",
      bairro: "",
      cidade: "",
      uf: "",
      complemento: "",
      // Contatos
      phones: [{ ddd: "", celular: "" }],
      emails: [{ email: "", tipo: "COMERCIAL" }],
      // externos (opcionais)
      _externalSend: false,
      _externalUser: "",
      _externalPass: "",
      _externalUrl: "",
    },
  });

  const {
    data: editDetail,
    isLoading: isEditLoading,
    isError: isEditError,
  } = useQuery(
    ["negative-edit", editId],
    async () => {
      const { data } = await axios.get(NEGATIVE.GET(editId));
      return data;
    },
    { enabled: Boolean(editId) }
  );

  const [didPrefill, setDidPrefill] = React.useState(false);
  const buildPhonesFromDetail = (detail) => {
    const pairs = [
      [detail?.ddd1, detail?.celular1],
      [detail?.ddd2, detail?.celular2],
      [detail?.ddd3, detail?.celular3],
    ];
    const list = pairs
      .map(([ddd, cel]) => {
        const d = onlyDigits(ddd || "").slice(0, 2);
        const c = onlyDigits(cel || "").slice(0, 9);
        if (!d && !c) return null;
        return { ddd: d, celular: c };
      })
      .filter(Boolean);
    if (list.length === 0 && (detail?.ddd || detail?.telefone)) {
      list.push({
        ddd: onlyDigits(detail?.ddd || "").slice(0, 2),
        celular: onlyDigits(detail?.telefone || "").slice(0, 9),
      });
    }
    return list.length ? list : [{ ddd: "", celular: "" }];
  };
  const buildEmailsFromDetail = (detail) => {
    const pairs = [
      [detail?.email1, detail?.tipoEmail1],
      [detail?.email2, detail?.tipoEmail2],
      [detail?.email3, detail?.tipoEmail3],
    ];
    const list = pairs
      .map(([email, tipo]) => {
        const e = String(email || "").trim();
        if (!e) return null;
        return { email: e, tipo: String(tipo || "COMERCIAL").trim() };
      })
      .filter(Boolean);
    if (list.length === 0 && detail?.email) {
      list.push({ email: String(detail.email).trim(), tipo: "COMERCIAL" });
    }
    return list.length ? list : [{ email: "", tipo: "COMERCIAL" }];
  };

  React.useEffect(() => {
    if (!editDetail || didPrefill) return;
    const phones = buildPhonesFromDetail(editDetail);
    const emails = buildEmailsFromDetail(editDetail);
    reset({
      ...getValues(),
      cnpj: onlyCnpjChars(editDetail?.cnpj || ""),
      documentoDevedor: maskCNPJorCPF(editDetail?.documentoDevedor || ""),
      nome: editDetail?.nome || "",
      dataNascimento: editDetail?.dataNascimento
        ? toYMD(new Date(editDetail.dataNascimento))
        : "",
      contrato: editDetail?.contrato || "",
      valor: formatBRLMask(String(editDetail?.valor ?? "")),
      dataVencimento: editDetail?.dataVencimento
        ? toYMD(new Date(editDetail.dataVencimento))
        : vencMax,
      codigoNatureza: editDetail?.codigoNatureza || "",
      participacao: editDetail?.participacao || "",
      cep: editDetail?.cep ? maskCEP(String(editDetail.cep)) : "",
      endereco: editDetail?.endereco || "",
      numero: editDetail?.numero || "",
      bairro: editDetail?.bairro || "",
      cidade: editDetail?.cidade || "",
      uf: editDetail?.uf || "",
      complemento: editDetail?.complemento || "",
      phones,
      emails,
      bureaus: Array.isArray(editDetail?.bureaus)
        ? editDetail.bureaus
        : getValues("bureaus"),
    });
    setDidPrefill(true);
  }, [editDetail, didPrefill, getValues, reset, vencMax]);

  const naturezaValue = useWatch({ control, name: "codigoNatureza" });
  const naturezaQuery = String(naturezaValue || "").toUpperCase();
  const naturezaOptions = React.useMemo(() => {
    const q = naturezaQuery.trim();
    if (!q) return NATUREZAS.slice(0, NATUREZA_MAX_RESULTS);
    const res = NATUREZAS.filter((n) => {
      const code = String(n.Codigo || "").toUpperCase();
      const desc = String(n.Descricao || "").toUpperCase();
      const sigla = String(n.Sigla || "").toUpperCase();
      return (
        code.includes(q) || desc.includes(q) || (sigla && sigla.includes(q))
      );
    });
    return res.slice(0, NATUREZA_MAX_RESULTS);
  }, [naturezaQuery]);

  const {
    fields: phoneFields,
    append: appendPhone,
    remove: removePhone,
  } = useFieldArray({
    control,
    name: "phones",
  });

  const {
    fields: emailFields,
    append: appendEmail,
    remove: removeEmail,
  } = useFieldArray({
    control,
    name: "emails",
  });

  const [showCancelConfirm, setShowCancelConfirm] = React.useState(false);

  const docWatch = useWatch({ control, name: "documentoDevedor" });
  const isCPFValido = React.useMemo(() => {
    return getDocType(docWatch) === "CPF" && isValidCPF(docWatch);
  }, [docWatch]);

  const [
    bureausValue,
    cnpjValue,
    documentoValue,
    nomeValue,
    dataNascimentoValue,
    contratoValue,
    codigoNaturezaValue,
    participacaoValue,
    dataVencimentoValue,
    valorValue,
    cepValue,
    enderecoValue,
    numeroValue,
    bairroValue,
    complementoValue,
    cidadeValue,
    ufValue,
    phonesValue,
    emailsValue,
  ] = useWatch({
    control,
    name: [
      "bureaus",
      "cnpj",
      "documentoDevedor",
      "nome",
      "dataNascimento",
      "contrato",
      "codigoNatureza",
      "participacao",
      "dataVencimento",
      "valor",
      "cep",
      "endereco",
      "numero",
      "bairro",
      "complemento",
      "cidade",
      "uf",
      "phones",
      "emails",
    ],
  });

  const selectedBureaus = Array.isArray(bureausValue) ? bureausValue : [];
  const selectedBureausData = React.useMemo(
    () => BUREAUS.filter((b) => selectedBureaus.includes(b.value)),
    [selectedBureaus]
  );
  const formatPhoneDisplay = (p) => {
    const d = onlyDigits(p?.ddd || "");
    const c = onlyDigits(p?.celular || "");
    if (!d && !c) return "";
    if (d && c) return `(${d}) ${c}`;
    return c || d;
  };
  const phonesReview = React.useMemo(() => {
    if (!Array.isArray(phonesValue)) return [];
    return phonesValue
      .map((p) => formatPhoneDisplay(p))
      .filter((v) => v);
  }, [phonesValue]);
  const emailsReview = React.useMemo(() => {
    if (!Array.isArray(emailsValue)) return [];
    return emailsValue
      .map((e) => ({
        email: String(e?.email || "").trim(),
        tipo: String(e?.tipo || "").trim(),
      }))
      .filter((e) => e.email);
  }, [emailsValue]);

  const steps = [
    {
      key: "principal",
      title: "Dados principais",
      description: "Informações do devedor e da dívida.",
    },
    {
      key: "endereco",
      title: "Endereço",
      description: "Localização do devedor.",
    },
    {
      key: "contatos",
      title: "Contatos",
      description: "Telefones e e-mails para contato.",
    },
    {
      key: "revisao",
      title: "Revisão",
      description: "Confira tudo antes de enviar.",
    },
  ];
  const [step, setStep] = React.useState(0);
  const stepFields = React.useMemo(() => {
    const step1 = [
      "bureaus",
      "cnpj",
      "documentoDevedor",
      "nome",
      "contrato",
      "codigoNatureza",
      "participacao",
      "dataVencimento",
      "valor",
    ];
    if (isCPFValido) step1.push("dataNascimento");
    const step2 = [
      "cep",
      "endereco",
      "numero",
      "bairro",
      "cidade",
      "uf",
      "complemento",
    ];
    const step3 = ["phones", "emails"];
    const step4 = [];
    return [step1, step2, step3, step4];
  }, [isCPFValido]);

  const handleNextStep = async () => {
    const fields = stepFields[step] || [];
    const ok = await trigger(fields, { shouldFocus: true });
    if (ok) setStep((s) => Math.min(s + 1, steps.length - 1));
  };
  const handlePrevStep = () =>
    setStep((s) => Math.max(s - 1, 0));

  React.useEffect(() => {
    if (!user?.Company?.document) return;
    const doc = onlyCnpjChars(user.Company.document);
    if (doc && isValidCNPJ(doc)) {
      setValue("cnpj", doc);
    }
  }, [user, setValue]);

  // ===== Auto-lookup OpenCNPJ quando documento é CNPJ válido =====
  React.useEffect(() => {
    const controller = new AbortController();
    const run = async () => {
      const raw = onlyCnpjChars(docWatch);
      setCnpjLookupError("");
      if (getDocType(docWatch) !== "CNPJ") return; // só tenta para CNPJ
      if (!isValidCNPJ(raw)) return;
      try {
        setCnpjLookupLoading(true);
        const res = await fetch(`https://api.opencnpj.org/${raw}`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error("Falha ao consultar OpenCNPJ");
        const data = await res.json();

        // Preenchimento "se vazio"
        const currentNome = getValues("nome");
        if (!currentNome) {
          const composedNome = (data?.razao_social || data?.nome_fantasia || "")
            .toString()
            .trim();
          if (composedNome) setValue("nome", composedNome.slice(0, 70));
        }

        const currentEndereco = getValues("endereco");
        if (!currentEndereco) {
          const baseEnd = [data?.logradouro, data?.numero, data?.complemento]
            .filter(Boolean)
            .join(", ");
          if (baseEnd) setValue("endereco", String(baseEnd).slice(0, 45));
        }

        const currentBairro = getValues("bairro");
        if (!currentBairro && data?.bairro) {
          setValue("bairro", String(data.bairro).slice(0, 20));
        }

        const currentCidade = getValues("cidade");
        if (!currentCidade && data?.municipio) {
          setValue("cidade", String(data.municipio).slice(0, 25));
        }

        const currentCEP = getValues("cep");
        if (!currentCEP && data?.cep) {
          setValue("cep", maskCEP(String(data.cep)));
        }

        const currentUF = getValues("uf");
        if (!currentUF && data?.uf) {
          setValue("uf", maskUF(String(data.uf)));
        }

        const currentEmail = getValues("emails.0.email");
        if (!currentEmail && data?.email) {
          setValue("emails.0.email", String(data.email).trim());
        }

        const currentDDD = getValues("phones.0.ddd");
        const currentTel = getValues("phones.0.celular");
        if (
          (!currentDDD || !currentTel) &&
          Array.isArray(data?.telefones) &&
          data.telefones.length
        ) {
          const t = data.telefones[0];
          const ddd = onlyDigits(t.ddd || "").slice(0, 2);
          const numero = onlyDigits(t.numero || "").slice(0, 9);
          if (ddd && numero) {
            setValue("phones.0.ddd", ddd);
            setValue("phones.0.celular", numero);
          }
        }
      } catch (e) {
        if (e?.name !== "AbortError") {
          setCnpjLookupError(
            e?.message || "Erro ao consultar dados do CNPJ (OpenCNPJ)."
          );
        }
      } finally {
        setCnpjLookupLoading(false);
      }
    };
    run();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docWatch, setValue, getValues]);

  const mutation = useMutation(
    async (payload) => {
      const { data } = isEditMode
        ? await axios.patch(NEGATIVE.UPDATE(editId), payload)
        : await axios.post(NEGATIVE.CREATE, payload);
      return data;
    },
    {
      onSuccess: () => {
        setSuccessMsg(
          isEditMode
            ? "Negativação atualizada com sucesso!"
            : "Negativação criada com sucesso!"
        );
      },
    }
  );

  const onSubmit = async (values) => {
    try {
      // contrato uppercase e regex
      const contratoUpper = String(values.contrato || "")
        .toUpperCase()
        .trim();
      if (!CONTRATO_ALLOWED.test(contratoUpper)) {
        throw new Error(
          "Contrato inválido: use até 16 caracteres 0-9 A-Z / - ."
        );
      }

      // valor com 2 casas
      const val = parseBRLToNumber(values.valor);
      const valor2 = Math.round(val * 100) / 100;

      const phones = Array.isArray(values.phones) ? values.phones : [];
      const emails = Array.isArray(values.emails) ? values.emails : [];

      const normalizePhone = (p) => ({
        ddd: onlyDigits(p?.ddd).slice(0, 2),
        celular: onlyDigits(p?.celular).slice(0, 9),
      });
      const normalizeEmail = (e) => ({
        email: String(e?.email || "").trim(),
        tipo: String(e?.tipo || "").trim(),
      });

      const p1 = normalizePhone(phones[0]);
      const p2 = normalizePhone(phones[1]);
      const p3 = normalizePhone(phones[2]);
      const e1 = normalizeEmail(emails[0]);
      const e2 = normalizeEmail(emails[1]);
      const e3 = normalizeEmail(emails[2]);

      const {
        companyId,
        _externalSend,
        _externalUser,
        _externalPass,
        _externalUrl,
        ...restValues
      } = values;

      const payload = {
        ...restValues,
        contrato: contratoUpper,
        cnpj: onlyCnpjChars(values.cnpj),
        documentoDevedor: onlyCnpjChars(values.documentoDevedor),
        cep: onlyDigits(values.cep),
        participacao: String(values.participacao || "").trim(),
        ddd1: p1.ddd,
        celular1: p1.celular,
        ddd2: p2.ddd,
        celular2: p2.celular,
        ddd3: p3.ddd,
        celular3: p3.celular,
        email1: e1.email,
        tipoEmail1: e1.tipo,
        email2: e2.email,
        tipoEmail2: e2.tipo,
        email3: e3.email,
        tipoEmail3: e3.tipo,
        valor: valor2,
        ddd: p1.ddd,
        telefone: p1.celular,
        email: e1.email,
      };

      if (isEditMode && normalizeStatus(editDetail?.status) === "PENDENTE") {
        payload.status = "ABERTO";
      }

      await mutation.mutateAsync(payload);
    } catch (error) {
      // Alert de erro já cobre
    }
  };

  const handleCepBlur = async () => {
    const cepDigits = onlyDigits(getValues("cep") || "");
    setCepError("");
    if (cepDigits.length !== 8) {
      if (cepDigits.length > 0) setCepError("CEP deve ter 8 dígitos.");
      return;
    }
    try {
      setCepLoading(true);
      const data = await lookupCEP(cepDigits);
      const endereco = getValues("endereco");
      const bairro = getValues("bairro");
      const cidade = getValues("cidade");
      const uf = getValues("uf");
      if (!endereco) setValue("endereco", data.logradouro || "");
      if (!bairro) setValue("bairro", data.bairro || "");
      if (!cidade) setValue("cidade", data.localidade || "");
      if (!uf) setValue("uf", data.uf || "");
    } catch (e) {
      setCepError(e?.message || "Erro ao consultar CEP");
    } finally {
      setCepLoading(false);
    }
  };

  const isSubmittingAny = isSubmitting || mutation.isLoading;

  const onCancel = () => {
    if (isSubmittingAny) return;
    if (isDirty) {
      setShowCancelConfirm(true);
    } else {
      navigate(-1);
    }
  };

  if (!user?.Company?.hasCV) {
    return (
      <div className="relative">
        <ForbiddenQueriesPermission />
      </div>
    );
  }

  if (successMsg) {
    return (
      <div className="p-4 md:p-6 max-w-3xl mx-auto">
        <Card className="shadow-sm border-0">
          <div className="flex items-center gap-3 mb-4">
            <HiCheckCircle className="text-green-500 w-7 h-7" />
            <div>
              <h2 className="text-xl font-semibold">
                {isEditMode
                  ? "Negativação atualizada com sucesso"
                  : "Solicitação enviada com sucesso"}
              </h2>
              <p className="text-sm text-gray-500">
                {isEditMode
                  ? "As alterações foram salvas e estão em processamento."
                  : "A solicitação de inclusão foi enviada e está em processamento."}
              </p>
            </div>
          </div>

          <div className="rounded-lg bg-gray-50 border border-gray-200 p-4 text-sm text-gray-700">
            <p className="font-medium mb-2">O que fazer agora</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Aguarde a confirmação da inclusão.</li>
              <li>
                Você pode acompanhar o status na listagem de negativações.
              </li>
            </ul>
          </div>

          <div className="mt-5 flex justify-center">
            <Button
              onClick={() =>
                navigate("/negatives", { state: { refetch: true } })
              }
            >
              Ir para listagem
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
              {isEditMode ? "Editar Negativação" : "Nova Negativação"}
            </h1>
            <p className="text-sm text-gray-500">
              {isEditMode
                ? "Atualize os campos necessários e salve as alterações."
                : "Inclua uma nova negativação preenchendo os campos abaixo."}
            </p>
          </div>
          <HiInformationCircle className="text-gray-400 w-6 h-6" />
        </div>
        {isEditMode && isEditLoading && (
          <Alert color="info" className="mb-4">
            Carregando dados da negativação...
          </Alert>
        )}
        {isEditMode && isEditError && (
          <Alert color="failure" className="mb-4">
            Não foi possível carregar os dados para edição.
          </Alert>
        )}
        <div className="relative overflow-hidden rounded-lg">
          {/* Wizard */}
          <div className="mb-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">
                  Etapa {step + 1} de {steps.length}
                </div>
                <div className="text-lg font-semibold text-gray-900">
                  {steps[step]?.title}
                </div>
                <div className="text-sm text-gray-500">
                  {steps[step]?.description}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {steps.map((s, idx) => {
                  const active = idx === step;
                  const completed = idx < step;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => {
                        if (idx <= step) setStep(idx);
                      }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition ${
                        active
                          ? "border-blue-400 bg-blue-50 text-blue-700"
                          : completed
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : "border-gray-200 text-gray-400"
                      }`}
                    >
                      <span
                        className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                          active
                            ? "bg-blue-600 text-white"
                            : completed
                            ? "bg-emerald-500 text-white"
                            : "bg-gray-200 text-gray-500"
                        }`}
                      >
                        {completed ? (
                          <HiCheckCircle className="h-4 w-4" />
                        ) : (
                          idx + 1
                        )}
                      </span>
                      <span className="hidden md:inline">{s.title}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-3 h-1.5 w-full rounded-full bg-gray-200">
              <div
                className="h-1.5 rounded-full bg-blue-600 transition-all"
                style={{
                  width: `${
                    steps.length > 1
                      ? (step / (steps.length - 1)) * 100
                      : 0
                  }%`,
                }}
              />
            </div>
          </div>

          {/* Seção: Devedor & Título */}
          {step === 0 && (
            <Card className="shadow-sm border-0 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <HiLightningBolt className="text-blue-500" />
              <h2 className="text-lg font-semibold">Dados principais</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Birôs */}
              <Controller
                name="bureaus"
                control={control}
                rules={{
                  validate: (v) =>
                    (Array.isArray(v) && v.length > 0) ||
                    "Selecione pelo menos um birô.",
                }}
                render={({ field }) => {
                  const selected = Array.isArray(field.value)
                    ? field.value
                    : [];
                  const toggle = (code) => {
                    if (!code) return;
                    if (selected.includes(code)) {
                      field.onChange(selected.filter((c) => c !== code));
                    } else {
                      field.onChange([...selected, code]);
                    }
                  };
                  return (
                    <div className="md:col-span-3">
                      <Label className="mb-1 block">Birô(s) para inclusão da negativação *</Label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {BUREAUS.map((b) => {
                          const active = selected.includes(b.value);
                          const disabled = !b.available;
                          return (
                            <label
                              key={b.value}
                              className={`flex items-center gap-3 rounded-xl border p-3 shadow-sm transition ${
                                active
                                  ? "border-blue-400 bg-blue-50/60"
                                  : "border-gray-200 bg-white"
                              } ${
                                disabled
                                  ? "opacity-60 cursor-not-allowed"
                                  : "cursor-pointer hover:border-blue-300"
                              }`}
                            >
                              <input
                                type="checkbox"
                                className="sr-only"
                                checked={active}
                                disabled={disabled}
                                onChange={() => {
                                  if (!disabled) toggle(b.value);
                                }}
                                onBlur={field.onBlur}
                              />
                              <span
                                className={`flex h-5 w-5 items-center justify-center rounded-md border ${
                                  active
                                    ? "bg-blue-600 border-blue-600"
                                    : "border-gray-300 bg-white"
                                }`}
                              >
                                {active && (
                                  <span className="h-2 w-2 rounded-sm bg-white" />
                                )}
                              </span>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-gray-800">
                                    {b.label}
                                  </span>
                                  {!b.available && (
                                    <span className="text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                                      Em breve
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {b.description}
                                </p>
                              </div>
                              <div className="ml-auto w-20 h-10 rounded-md border border-gray-200 bg-white flex items-center justify-center overflow-hidden">
                                <img
                                  src={b.logo}
                                  alt={`Logo ${b.label}`}
                                  className="h-6 w-auto object-contain"
                                />
                              </div>
                            </label>
                          );
                        })}
                      </div>
                      {errors.bureaus?.message && (
                        <div className="text-xs text-red-600 mt-1">
                          {errors.bureaus.message}
                        </div>
                      )}
                    </div>
                  );
                }}
              />

              {/* CNPJ */}
              <Controller
                name="cnpj"
                control={control}
                rules={{
                  validate: (value) => {
                    const d = onlyCnpjChars(value);
                    if (!d) return true;
                    return isValidCNPJ(d) || "CNPJ inválido.";
                  },
                }}
                render={({ field }) => (
                  <div>
                    <Label htmlFor="field-cnpj" className="mb-1 block">
                      CNPJ do credor
                    </Label>
                    <TextInput
                      id="field-cnpj"
                      value={maskCNPJ(field.value)}
                      onChange={(e) => field.onChange(e.target.value)}
                      onBlur={field.onBlur}
                      color={errors.cnpj ? "failure" : undefined}
                      helperText={errors.cnpj?.message}
                    />
                  </div>
                )}
              />

              {/* Documento Devedor */}
              <Controller
                name="documentoDevedor"
                control={control}
                rules={{
                  required: "Documento é obrigatório.",
                  validate: (value) => {
                    if (!onlyCnpjChars(value)) return "Informe CPF ou CNPJ.";
                    return getDocType(value) === "CPF"
                      ? isValidCPF(value) || "CPF inválido"
                      : isValidCNPJ(value) || "CNPJ inválido";
                  },
                }}
                render={({ field }) => (
                  <div>
                    <Label
                      htmlFor="field-documentoDevedor"
                      className="mb-1 block"
                    >
                      Documento do Devedor *
                    </Label>
                    <TextInput
                      id="field-documentoDevedor"
                      value={field.value}
                      onChange={(e) =>
                        field.onChange(maskCNPJorCPF(e.target.value))
                      }
                      onBlur={field.onBlur}
                      placeholder="CPF/CNPJ"
                      color={
                        errors.documentoDevedor || cnpjLookupError
                          ? "failure"
                          : cnpjLookupLoading
                          ? "info"
                          : undefined
                      }
                      helperText={
                        (
                          <span className="text-xs block -mt-1">
                            {errors.documentoDevedor?.message}
                          </span>
                        ) ||
                        (cnpjLookupLoading ? (
                          <span className="text-xs block -mt-1">
                            Consultando dados do CNPJ...
                          </span>
                        ) : (
                          cnpjLookupError || ""
                        ))
                      }
                      required
                    />
                  </div>
                )}
              />

              {/* Nome Principal */}
              <Controller
                name="nome"
                control={control}
                rules={{
                  required: "Nome completo é obrigatório.",
                  validate: (v) => {
                    const s = String(v || "").trim();
                    return (
                      (s.length > 0 && s.length <= 70) || "Máx. 70 caracteres."
                    );
                  },
                }}
                render={({ field }) => (
                  <div>
                    <Label htmlFor="field-nome" className="mb-1 block">
                      Nome Completo *
                    </Label>
                    <TextInput
                      id="field-nome"
                      {...field}
                      placeholder="Nome completo"
                      maxLength={70}
                    />
                    {errors.nome?.message && (
                      <div className="text-xs text-red-600 mt-1">
                        {errors.nome.message}
                      </div>
                    )}
                  </div>
                )}
              />

              {/* Data Nascimento (apenas CPF válido) */}
              {isCPFValido && (
                <Controller
                  name="dataNascimento"
                  control={control}
                  rules={{
                    validate: (v) => {
                      if (!v)
                        return "Data de nascimento é obrigatória para CPF.";
                      return isAdult(v) || "Devedor deve ter 18 anos ou mais.";
                    },
                  }}
                  render={({ field }) => (
                    <div>
                      <Label className="mb-1 block">Dt. Nascimento</Label>
                      <TextInput type="date" {...field} />
                      {errors.dataNascimento?.message && (
                        <div className="text-xs text-red-600 mt-1">
                          {errors.dataNascimento.message}
                        </div>
                      )}
                    </div>
                  )}
                />
              )}

              {/* Contrato */}
              <Controller
                name="contrato"
                control={control}
                rules={{
                  required: "Contrato é obrigatório.",
                  validate: (v) => {
                    const s = String(v || "")
                      .toUpperCase()
                      .trim();
                    return (
                      CONTRATO_ALLOWED.test(s) ||
                      "Use até 16 caracteres: 0-9 A-Z / - ."
                    );
                  },
                }}
                render={({ field }) => (
                  <div>
                    <Label htmlFor="field-contrato" className="mb-1 block">
                      Nro. Contrato *
                    </Label>
                    <TextInput
                      id="field-contrato"
                      {...field}
                      maxLength={16}
                      color={errors.contrato ? "failure" : undefined}
                      helperText={
                        <span className="block text-xs -mt-1">
                          {errors.contrato?.message}
                        </span>
                      }
                      required
                    />
                    <span className="text-2xs italic text-gray-500 block leadin-none mt-0.5">
                      Número de Contrato, Nota Fiscal, Boletos, Duplicatas, etc.
                    </span>
                  </div>
                )}
              />

              {/* Natureza */}
              <Controller
                name="codigoNatureza"
                control={control}
                rules={{
                  required: "Código natureza é obrigatório.",
                  validate: (v) => {
                    const code = String(v || "")
                      .trim()
                      .toUpperCase();
                    if (!code) return "Código natureza é obrigatório.";
                    return !!NATUREZA_BY_CODE[code] || "Natureza inválida.";
                  },
                }}
                render={({ field }) => (
                  <div>
                    <Label htmlFor="field-codnat" className="mb-1 block">
                      Natureza *
                    </Label>
                    <div className="relative">
                      <TextInput
                        id="field-codnat"
                        value={String(field.value || "")}
                        onChange={(e) =>
                          field.onChange(e.target.value.toUpperCase())
                        }
                        onFocus={() => setNaturezaOpen(true)}
                        onBlur={(e) => {
                          field.onBlur();
                          setTimeout(() => setNaturezaOpen(false), 120);
                        }}
                        placeholder="Digite o código ou descrição (ex: ABI)"
                        color={errors.codigoNatureza ? "failure" : undefined}
                        required
                      />
                      {naturezaOpen && (
                        <div className="absolute z-20 mt-1 w-full max-h-60 overflow-auto rounded-md border border-gray-200 bg-white shadow-sm">
                          {naturezaOptions.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-gray-500">
                              Nenhuma natureza encontrada
                            </div>
                          ) : (
                            naturezaOptions.map((n) => (
                              <button
                                key={`${n.Codigo}-${n.Sigla}`}
                                type="button"
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  field.onChange(String(n.Codigo || ""));
                                  setNaturezaOpen(false);
                                }}
                              >
                                <span className="font-medium">{n.Codigo}</span>{" "}
                                — {n.Descricao}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    {errors.codigoNatureza?.message && (
                      <div className="text-xs text-red-600 mt-1">
                        {errors.codigoNatureza.message}
                      </div>
                    )}
                    {!errors.codigoNatureza &&
                      NATUREZA_BY_CODE[String(field.value || "").toUpperCase()]
                        ?.Descricao && (
                        <div className="text-xs text-gray-500 mt-1">
                          {
                            NATUREZA_BY_CODE[
                              String(field.value || "").toUpperCase()
                            ].Descricao
                          }
                        </div>
                      )}
                  </div>
                )}
              />

              {/* Participação */}
              <Controller
                name="participacao"
                control={control}
                rules={{ required: "Participação é obrigatória." }}
                render={({ field }) => (
                  <div>
                    <Label htmlFor="field-participacao" className="mb-1 block">
                      Participação
                    </Label>
                    <Select
                      id="field-participacao"
                      value={String(field.value || "")}
                      onChange={(e) => field.onChange(e.target.value)}
                      onBlur={field.onBlur}
                      color={errors.participacao ? "failure" : undefined}
                    >
                      <option value="">Selecione...</option>
                      <option value="P">Devedor Principal</option>
                      <option value="A">Avalista</option>
                    </Select>
                    {errors.participacao?.message && (
                      <div className="text-xs text-red-600 mt-1">
                        {errors.participacao.message}
                      </div>
                    )}
                  </div>
                )}
              />

              {/* Vencimento */}
              <Controller
                name="dataVencimento"
                control={control}
                rules={{
                  required: "Vencimento é obrigatório.",
                  validate: (v) => {
                    if (!v) return "Vencimento é obrigatório.";
                    const d = new Date(v);
                    if (Number.isNaN(d.getTime())) return "Data inválida.";
                    const min = new Date(vencMin);
                    const max = new Date(vencMax);
                    if (d < min || d > max) {
                      const fmt = (s) => {
                        const [yy, mm, dd] = s.split("-");
                        return `${dd}/${mm}/${yy}`;
                      };
                      return `A data do vencimento deve ser não superior a 4 anos e 11 meses (mín: ${fmt(
                        vencMin
                      )}) e inferior a 4 dias (máx: ${fmt(
                        vencMax
                      )}) em relação à data de hoje.`;
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <div>
                    <Label
                      htmlFor="field-dataVencimento"
                      className="mb-1 block"
                    >
                      Dt. Vencimento *
                    </Label>
                    <TextInput
                      id="field-dataVencimento"
                      type="date"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      min={vencMin}
                      max={vencMax}
                      color={errors.dataVencimento ? "failure" : undefined}
                      helperText={errors.dataVencimento?.message}
                      required
                    />
                  </div>
                )}
              />

              {/* Valor */}
              <Controller
                name="valor"
                control={control}
                rules={{
                  required: "Valor é obrigatório.",
                  validate: (v) => {
                    const n = parseBRLToNumber(v);
                    if (!Number.isFinite(n) || n <= 0)
                      return "Informe um valor válido.";
                    return true;
                  },
                }}
                render={({ field }) => (
                  <div>
                    <Label htmlFor="field-valor" className="mb-1 block">
                      Valor *
                    </Label>
                    <TextInput
                      id="field-valor"
                      type="text"
                      inputMode="numeric"
                      value={field.value}
                      onChange={(e) =>
                        field.onChange(formatBRLMask(e.target.value))
                      }
                      onBlur={field.onBlur}
                      placeholder="R$ 0,00"
                      color={errors.valor ? "failure" : undefined}
                      helperText={errors.valor?.message}
                      required
                    />
                  </div>
                )}
              />
            </div>
            </Card>
          )}

          {/* Endereço */}
          {step === 1 && (
            <Card className="shadow-sm border-0 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <HiLightningBolt className="text-blue-500" />
                <h2 className="text-lg font-semibold">Endereço</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* CEP */}
              <Controller
                name="cep"
                control={control}
                rules={{
                  required: "CEP é obrigatório.",
                  validate: (v) =>
                    onlyDigits(v).length === 8 || "CEP deve ter 8 dígitos.",
                }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">CEP *</Label>
                    <TextInput
                      id="field-cep"
                      value={field.value}
                      onChange={(e) => field.onChange(maskCEP(e.target.value))}
                      onBlur={async () => {
                        field.onBlur();
                        await handleCepBlur();
                      }}
                      placeholder="__.___-___"
                      color={cepError ? "failure" : null}
                      helperText={
                        cepError ? (
                          <span className="text-xs -mt-1 block">
                            CEP inválido
                          </span>
                        ) : cepLoading ? (
                          <span className="text-xs -mt-1 block">
                            Consultando CEP...
                          </span>
                        ) : field.value &&
                          onlyDigits(field.value).length === 8 ? (
                          <span className="text-xs -mt-1 block">
                            CEP Válido
                          </span>
                        ) : (
                          ""
                        )
                      }
                      required
                      maxLength={10}
                    />
                  </div>
                )}
              />

              {/* Endereco */}
              <Controller
                name="endereco"
                control={control}
                rules={{
                  required: "Logradouro é obrigatório.",
                  validate: (v) => {
                    const s = String(v || "").trim();
                    return (
                      (s.length > 0 && s.length <= 45) || "Máx. 45 caracteres."
                    );
                  },
                }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">Logradouro *</Label>
                    <TextInput
                      {...field}
                      placeholder="Rua/Av"
                      required
                      maxLength={45}
                    />
                    {errors.endereco?.message && (
                      <div className="text-xs text-red-600 mt-1">
                        {errors.endereco.message}
                      </div>
                    )}
                  </div>
                )}
              />

              <Controller
                name="numero"
                control={control}
                rules={{
                  required: "Número é obrigatório.",
                  validate: (v) => {
                    const s = String(v || "").trim();
                    return (
                      (s.length > 0 && s.length <= 20) || "Máx. 20 caracteres."
                    );
                  },
                }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">Número *</Label>
                    <TextInput {...field} required maxLength={20} />
                    {errors.numero?.message && (
                      <div className="text-xs text-red-600 mt-1">
                        {errors.numero.message}
                      </div>
                    )}
                  </div>
                )}
              />

              <Controller
                name="complemento"
                control={control}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">Complemento</Label>
                    <TextInput {...field} maxLength={20} />
                  </div>
                )}
              />

              {/* Bairro */}
              <Controller
                name="bairro"
                control={control}
                rules={{
                  required: "Bairro é obrigatório.",
                  validate: (v) => {
                    const s = String(v || "").trim();
                    return (
                      (s.length > 0 && s.length <= 20) || "Máx. 20 caracteres."
                    );
                  },
                }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">Bairro *</Label>
                    <TextInput {...field} required maxLength={20} />
                    {errors.bairro?.message && (
                      <div className="text-xs text-red-600 mt-1">
                        {errors.bairro.message}
                      </div>
                    )}
                  </div>
                )}
              />

              {/* Cidade */}
              <Controller
                name="cidade"
                control={control}
                rules={{
                  required: "Cidade é obrigatória.",
                  validate: (v) => {
                    const s = String(v || "").trim();
                    return (
                      (s.length > 0 && s.length <= 25) || "Máx. 25 caracteres."
                    );
                  },
                }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">Cidade *</Label>
                    <TextInput {...field} required maxLength={25} />
                    {errors.cidade?.message && (
                      <div className="text-xs text-red-600 mt-1">
                        {errors.cidade.message}
                      </div>
                    )}
                  </div>
                )}
              />

              {/* UF como Select */}
              <Controller
                name="uf"
                control={control}
                rules={{
                  required: "UF é obrigatória.",
                  validate: (v) =>
                    !v || v.length === 2 || "UF deve ter 2 letras.",
                }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">UF *</Label>
                    <Select
                      value={String(field.value || "")}
                      onChange={(e) =>
                        field.onChange(e.target.value.toUpperCase())
                      }
                      onBlur={field.onBlur}
                      color={errors.uf ? "failure" : undefined}
                      required
                    >
                      <option value="">Selecione...</option>
                      {UFS.map((uf) => (
                        <option key={uf} value={uf}>
                          {uf}
                        </option>
                      ))}
                    </Select>
                    {errors.uf?.message && (
                      <div className="text-xs text-red-600 mt-1">
                        {errors.uf.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
            </Card>
          )}

          {/* Contatos */}
          {step === 2 && (
            <Card className="shadow-sm border-0 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <HiLightningBolt className="text-blue-500" />
                <h2 className="text-lg font-semibold">Contatos</h2>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                    <div>
                      <div className="text-sm font-semibold text-slate-700">
                        Celulares
                      </div>
                      <div className="text-xs text-slate-500">
                        Máximo de 3 números
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 bg-white border border-slate-200 rounded-full px-2 py-0.5">
                        {phoneFields.length}/3
                      </span>
                      <Button
                        color="light"
                        size="xs"
                        onClick={() => appendPhone({ ddd: "", celular: "" })}
                        disabled={phoneFields.length >= 3}
                      >
                        Adicionar
                      </Button>
                    </div>
                  </div>
                  <div className="p-4 space-y-4">
                    {phoneFields.map((item, index) => (
                      <div
                        key={item.id}
                        className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-sm font-semibold text-slate-700">
                            Celular {index + 1}
                          </div>
                          <Button
                            color="light"
                            size="xs"
                            onClick={() => removePhone(index)}
                            disabled={phoneFields.length === 1}
                          >
                            Remover
                          </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Controller
                            name={`phones.${index}.ddd`}
                            control={control}
                            rules={{
                              validate: (v) => {
                                const ddd = onlyDigits(v);
                                const cel = onlyDigits(
                                  getValues(`phones.${index}.celular`)
                                );
                                if (!ddd && !cel) return true;
                                return (
                                  ddd.length === 2 || "DDD deve ter 2 dígitos."
                                );
                              },
                            }}
                            render={({ field }) => (
                              <div>
                                <Label className="mb-1 block">DDD</Label>
                                <TextInput
                                  value={field.value}
                                  onChange={(e) =>
                                    field.onChange(
                                      onlyDigits(e.target.value).slice(0, 2)
                                    )
                                  }
                                  onBlur={field.onBlur}
                                  inputMode="numeric"
                                  maxLength={2}
                                  color={
                                    errors?.phones?.[index]?.ddd
                                      ? "failure"
                                      : undefined
                                  }
                                  helperText={
                                    errors?.phones?.[index]?.ddd?.message
                                  }
                                />
                              </div>
                            )}
                          />

                          <Controller
                            name={`phones.${index}.celular`}
                            control={control}
                            rules={{
                              validate: (v) => {
                                const cel = onlyDigits(v);
                                const ddd = onlyDigits(
                                  getValues(`phones.${index}.ddd`)
                                );
                                if (!cel && !ddd) return true;
                                return (
                                  (cel.length >= 8 && cel.length <= 9) ||
                                  "Celular deve ter 8 ou 9 dígitos."
                                );
                              },
                            }}
                            render={({ field }) => (
                              <div>
                                <Label className="mb-1 block">Celular</Label>
                                <TextInput
                                  value={field.value}
                                  onChange={(e) =>
                                    field.onChange(
                                      onlyDigits(e.target.value).slice(0, 9)
                                    )
                                  }
                                  onBlur={field.onBlur}
                                  inputMode="numeric"
                                  maxLength={9}
                                  color={
                                    errors?.phones?.[index]?.celular
                                      ? "failure"
                                      : undefined
                                  }
                                  helperText={
                                    errors?.phones?.[index]?.celular?.message
                                  }
                                />
                              </div>
                            )}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                    <div>
                      <div className="text-sm font-semibold text-slate-700">
                        E-mails
                      </div>
                      <div className="text-xs text-slate-500">
                        Máximo de 3 e-mails
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 bg-white border border-slate-200 rounded-full px-2 py-0.5">
                        {emailFields.length}/3
                      </span>
                      <Button
                        color="light"
                        size="xs"
                        onClick={() =>
                          appendEmail({ email: "", tipo: "COMERCIAL" })
                        }
                        disabled={emailFields.length >= 3}
                      >
                        Adicionar
                      </Button>
                    </div>
                  </div>
                  <div className="p-4 space-y-4">
                    {emailFields.map((item, index) => (
                      <div
                        key={item.id}
                        className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-sm font-semibold text-slate-700">
                            Email {index + 1}
                          </div>
                          <Button
                            color="light"
                            size="xs"
                            onClick={() => removeEmail(index)}
                            disabled={emailFields.length === 1}
                          >
                            Remover
                          </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Controller
                            name={`emails.${index}.tipo`}
                            control={control}
                            rules={{
                              validate: (v) => {
                                const email = String(
                                  getValues(`emails.${index}.email`) || ""
                                ).trim();
                                const tipo = String(v || "").trim();
                                if (!email && !tipo) return true;
                                if (email && !tipo)
                                  return "Informe o tipo do email.";
                                if (tipo && !email) return "Informe o email.";
                                return true;
                              },
                            }}
                            render={({ field }) => (
                              <div>
                                <Label className="mb-1 block">Tipo-email</Label>
                                <Select
                                  value={String(field.value || "")}
                                  onChange={(e) =>
                                    field.onChange(e.target.value)
                                  }
                                  onBlur={field.onBlur}
                                  color={
                                    errors?.emails?.[index]?.tipo
                                      ? "failure"
                                      : undefined
                                  }
                                >
                                  <option value="">Selecione...</option>
                                  <option value="COMERCIAL">COMERCIAL</option>
                                  <option value="PESSOAL">PESSOAL</option>
                                </Select>

                                {errors?.emails?.[index]?.tipo?.message && (
                                  <div className="text-xs text-red-600 mt-1">
                                    {errors.emails[index].tipo.message}
                                  </div>
                                )}
                              </div>
                            )}
                          />
                          <Controller
                            name={`emails.${index}.email`}
                            control={control}
                            rules={{
                              setValueAs: (v) => (v ?? "").trim(),
                              validate: (s) =>
                                !s ||
                                /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) ||
                                "Email inválido.",
                            }}
                            render={({ field }) => (
                              <div>
                                <Label className="mb-1 block">Email</Label>
                                <TextInput
                                  type="email"
                                  value={field.value}
                                  onChange={field.onChange}
                                  onBlur={field.onBlur}
                                  placeholder="email@email.com"
                                  color={
                                    errors?.emails?.[index]?.email
                                      ? "failure"
                                      : undefined
                                  }
                                  helperText={
                                    errors?.emails?.[index]?.email?.message
                                  }
                                />
                              </div>
                            )}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
          </Card>
          )}

          {step === 3 && (
            <Card className="shadow-sm border-0 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <HiCheckCircle className="text-emerald-500" />
                <h2 className="text-lg font-semibold">Revisão</h2>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="text-xs text-slate-500">
                    Birôs selecionados
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedBureausData.length > 0 ? (
                      selectedBureausData.map((b) => (
                        <div
                          key={b.value}
                          className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
                        >
                          <img
                            src={b.logo}
                            alt={`Logo ${b.label}`}
                            className="h-5 w-auto object-contain"
                          />
                          <span className="text-sm font-medium">{b.label}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-gray-500">—</div>
                    )}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="text-xs text-slate-500">Devedor</div>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs text-gray-500">Nome</div>
                      <div className="font-medium">
                        {nomeValue || "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Documento</div>
                      <div className="font-medium">
                        {maskCNPJorCPF(documentoValue) || "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">
                        Dt. Nascimento
                      </div>
                      <div className="font-medium">
                        {dataNascimentoValue
                          ? formatDateBR(dataNascimentoValue)
                          : "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Participação</div>
                      <div className="font-medium">
                        {participacaoValue === "P"
                          ? "Devedor Principal"
                          : participacaoValue === "A"
                          ? "Avalista"
                          : "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="text-xs text-slate-500">Título</div>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs text-gray-500">Contrato</div>
                      <div className="font-medium">
                        {contratoValue || "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Natureza</div>
                      <div className="font-medium">
                        {codigoNaturezaValue || "—"}
                      </div>
                      {codigoNaturezaValue &&
                        NATUREZA_BY_CODE[
                          String(codigoNaturezaValue || "").toUpperCase()
                        ]?.Descricao && (
                          <div className="text-xs text-gray-500">
                            {
                              NATUREZA_BY_CODE[
                                String(codigoNaturezaValue || "").toUpperCase()
                              ].Descricao
                            }
                          </div>
                        )}
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Vencimento</div>
                      <div className="font-medium">
                        {formatDateBR(dataVencimentoValue)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Valor</div>
                      <div className="font-medium">
                        {valorValue || "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="text-xs text-slate-500">Endereço</div>
                  <div className="mt-2 text-sm text-gray-700">
                    {enderecoValue
                      ? `${enderecoValue}${
                          numeroValue ? `, ${numeroValue}` : ""
                        }`
                      : "—"}
                    {complementoValue ? ` — ${complementoValue}` : ""}
                  </div>
                  <div className="text-sm text-gray-700">
                    {bairroValue ? `${bairroValue} · ` : ""}
                    {cidadeValue || "—"}
                    {ufValue ? `/${ufValue}` : ""}
                  </div>
                  <div className="text-sm text-gray-700">
                    {cepValue ? `CEP ${cepValue}` : ""}
                  </div>
                  <div className="text-sm text-gray-700">
                    {cnpjValue ? `CNPJ credor: ${maskCNPJ(cnpjValue)}` : ""}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm xl:col-span-2">
                  <div className="text-xs text-slate-500">Contatos</div>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-gray-500">Celulares</div>
                      <div className="mt-1 space-y-1">
                        {phonesReview.length > 0 ? (
                          phonesReview.map((p, idx) => (
                            <div
                              key={`${p}-${idx}`}
                              className="text-sm text-gray-700"
                            >
                              {p}
                            </div>
                          ))
                        ) : (
                          <div className="text-sm text-gray-500">—</div>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">E-mails</div>
                      <div className="mt-1 space-y-1">
                        {emailsReview.length > 0 ? (
                          emailsReview.map((e, idx) => (
                            <div
                              key={`${e.email}-${idx}`}
                              className="text-sm text-gray-700"
                            >
                              {e.email}
                              {e.tipo ? (
                                <span className="text-xs text-gray-400">
                                  {" "}
                                  · {e.tipo}
                                </span>
                              ) : null}
                            </div>
                          ))
                        ) : (
                          <div className="text-sm text-gray-500">—</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>

        {mutation.isError && (
          <Alert color="failure" className="mt-1" icon={HiInformationCircle}>
            {mutation.error?.response?.data?.error ||
              mutation.error?.message ||
              "Erro ao criar negativação"}
          </Alert>
        )}

        {/* Barra de ações */}
        <div className="mt-2">
          <div className="max-w-7xl mx-auto rounded-xl border border-slate-200 bg-white/90 backdrop-blur p-3 shadow-sm">
            <div className="flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-2">
                <Button
                  color="light"
                  onClick={onCancel}
                  disabled={isSubmittingAny}
                >
                  <div className="flex items-center gap-2">
                    <HiX />
                    Cancelar
                  </div>
                </Button>
                <span className="text-xs text-gray-500 hidden md:inline">
                  Você pode revisar antes de enviar.
                </span>
              </div>

              <div className="flex items-center gap-2 justify-end">
                {step > 0 && (
                  <Button
                    color="light"
                    onClick={handlePrevStep}
                    disabled={isSubmittingAny}
                  >
                    <div className="flex items-center gap-2">
                      <HiArrowLeft />
                      Voltar
                    </div>
                  </Button>
                )}
                {step < steps.length - 1 ? (
                  <Button onClick={handleNextStep} disabled={isSubmittingAny}>
                    <div className="flex items-center gap-2">
                      Próximo
                      <HiArrowRight />
                    </div>
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit(onSubmit)}
                    isProcessing={isSubmittingAny}
                  >
                    {isEditMode ? "Salvar alterações" : "Enviar negativação"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de confirmação de cancelamento */}
      <Modal
        show={showCancelConfirm}
        size="md"
        onClose={() => setShowCancelConfirm(false)}
      >
        <Modal.Header>Descartar alterações?</Modal.Header>
        <Modal.Body>
          <p className="text-sm text-gray-600">
            Você inseriu dados no formulário. Se sair agora, as mudanças serão
            perdidas.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button color="light" onClick={() => setShowCancelConfirm(false)}>
              Continuar editando
            </Button>
            <Button
              color="failure"
              onClick={() => {
                setShowCancelConfirm(false);
                navigate(-1);
              }}
            >
              Descartar alterações
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
